// Brokerage Service - API calls for brokerage account management and execution

import { useAuthStore } from "../stores/authStore";
import { useSessionExpiredStore } from "../stores/sessionExpiredStore";

const API_BASE = import.meta.env.VITE_AUTH_API_URL || "http://localhost:8081";

// =============================================================================
// Types
// =============================================================================

export type BrokerageAccountType = "paper" | "live";
export type BrokerageAccountStatus = "pending" | "active" | "error" | "suspended";

export interface BrokerageAccount {
  id: string;
  user_id: string;
  broker_name: string;
  account_name: string;
  account_type: BrokerageAccountType;
  alpaca_account_id?: string;
  alpaca_account_status?: string;
  is_default: boolean;
  fractional_enabled: boolean;
  status: BrokerageAccountStatus;
  verified_at?: string;
  created_at: string;
  updated_at: string;
  portfolio_value?: string;
  cash_balance?: string;
  buying_power?: string;
}

export interface CreateBrokerageAccountRequest {
  account_name: string;
  account_type: BrokerageAccountType;
  api_key: string;
  api_secret: string;
  is_default?: boolean;
  fractional_enabled?: boolean;
}

export interface UpdateBrokerageAccountRequest {
  account_name?: string;
  api_key?: string;
  api_secret?: string;
  fractional_enabled?: boolean;
}

export interface ValidateCredentialsRequest {
  api_key: string;
  api_secret: string;
  account_type: BrokerageAccountType;
}

export interface ValidateCredentialsResponse {
  valid: boolean;
  account_id?: string;
  account_status?: string;
  buying_power?: string;
  portfolio_value?: string;
  error?: string;
}

export interface AccountLimits {
  is_admin: boolean;
  max_paper: number;
  max_live: number;
  paper_count: number;
  live_count: number;
  can_add_paper: boolean;
  can_add_live: boolean;
}

export interface ListBrokerageAccountsResponse {
  accounts: BrokerageAccount[];
  account_limits: AccountLimits;
}

export interface Position {
  symbol: string;
  qty: string;
  avg_entry_price: string;
  market_value: string;
  current_price: string;
  unrealized_pl: string;
  unrealized_pl_pct: string;
  change_today: string;
}

export interface Order {
  id: string;
  symbol: string;
  qty?: string;
  notional?: string;
  side: string;
  type: string;
  status: string;
  filled_qty: string;
  filled_avg_price?: string;
  submitted_at: string;
  filled_at?: string;
}

export interface SchedulerStatus {
  running: boolean;
  enabled: boolean;
  dry_run: boolean;
  timezone: string;
  execution_cron: string;
  snapshot_cron: string;
  next_execution_time?: string;
  next_snapshot_time?: string;
  last_execution_run?: string;
  last_snapshot_run?: string;
  execution_timeout: string;
  snapshot_timeout: string;
}

export interface ExecutionLog {
  id: string;
  investment_id: string;
  brokerage_account_id: string;
  execution_date: string;
  execution_time: string;
  execution_type: string;
  portfolio_value_before: string;
  cash_balance_before: string;
  target_allocations?: Record<string, number>;
  current_allocations?: Record<string, number>;
  max_drift_percent: string;
  threshold_percent: string;
  rebalance_required: boolean;
  orders_planned?: ExecutionOrder[];
  orders_executed?: ExecutionOrder[];
  orders_failed?: ExecutionOrder[];
  status: string;
  error_message?: string;
  duration_ms?: number;
  portfolio_value_after?: string;
  cash_balance_after?: string;
  created_at: string;
}

export interface ExecutionOrder {
  symbol: string;
  side: string;
  notional: string;
  status: string;
  order_id?: string;
  filled_qty?: string;
  filled_avg_price?: string;
  error_message?: string;
}

export interface PerformanceSnapshot {
  id: string;
  investment_id: string;
  snapshot_date: string;
  portfolio_value: string;
  cash_balance: string;
  holdings_value: string;
  day_return: string;
  day_return_pct: string;
  total_return: string;
  total_return_pct: string;
  positions_count: number;
  max_drawdown_pct: string;
}

export interface InvestmentExecutionResult {
  investment_id: string;
  investment_name: string;
  success: boolean;
  skipped: boolean;
  skip_reason?: string;
  error?: string;
  reconciliation?: {
    drift_detected: boolean;
    adjustments: Array<{
      symbol: string;
      expected_shares: string;
      actual_shares: string;
      difference: string;
    }>;
  };
  intended_trades: Array<{
    symbol: string;
    action: string;
    shares: string;
    dollar_amount: string;
    current_weight: string;
    target_weight: string;
  }>;
  submitted_orders: Array<{
    symbol: string;
    side: string;
    shares: string;
    alpaca_id?: string;
    status: string;
    filled_qty: string;
    filled_price: string;
    error_message?: string;
  }>;
  shadow_updates: Array<{
    investment_id: string;
    symbol: string;
    prev_shares: string;
    new_shares: string;
    delta: string;
  }>;
  orders_submitted: number;
  orders_filled: number;
  orders_failed: number;
  market_open: boolean;
  is_first_execution: boolean;
}

// =============================================================================
// Helper Functions
// =============================================================================

function getAuthHeaders(): HeadersInit {
  const token = useAuthStore.getState().accessToken;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }));
    
    // Handle 401 Unauthorized - session expired
    if (response.status === 401) {
      console.warn("[brokerageService] 401 Unauthorized - triggering session expired modal");
      useSessionExpiredStore.getState().setExpired();
    }
    
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

// =============================================================================
// Brokerage Account API
// =============================================================================

export async function listBrokerageAccounts(): Promise<ListBrokerageAccountsResponse> {
  const response = await fetch(`${API_BASE}/api/v1/brokerage/accounts`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<ListBrokerageAccountsResponse>(response);
}

export async function getBrokerageAccount(accountId: string): Promise<BrokerageAccount> {
  const response = await fetch(`${API_BASE}/api/v1/brokerage/accounts/${accountId}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<BrokerageAccount>(response);
}

export async function createBrokerageAccount(
  request: CreateBrokerageAccountRequest
): Promise<BrokerageAccount> {
  const response = await fetch(`${API_BASE}/api/v1/brokerage/accounts`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });
  return handleResponse<BrokerageAccount>(response);
}

export async function updateBrokerageAccount(
  accountId: string,
  request: UpdateBrokerageAccountRequest
): Promise<BrokerageAccount> {
  const response = await fetch(`${API_BASE}/api/v1/brokerage/accounts/${accountId}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });
  return handleResponse<BrokerageAccount>(response);
}

export async function deleteBrokerageAccount(accountId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/v1/brokerage/accounts/${accountId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Delete failed" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
}

export async function setDefaultBrokerageAccount(accountId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/v1/brokerage/accounts/${accountId}/default`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Failed to set default" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
}

export async function validateCredentials(
  request: ValidateCredentialsRequest
): Promise<ValidateCredentialsResponse> {
  const response = await fetch(`${API_BASE}/api/v1/brokerage/accounts/validate`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });
  return handleResponse<ValidateCredentialsResponse>(response);
}

export async function verifyBrokerageAccount(
  accountId: string
): Promise<{ verified: boolean; account_id?: string; error?: string }> {
  const response = await fetch(`${API_BASE}/api/v1/brokerage/accounts/${accountId}/verify`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function syncBrokerageAccount(
  accountId: string
): Promise<{ synced: boolean; portfolio_value?: string; cash?: string }> {
  const response = await fetch(`${API_BASE}/api/v1/brokerage/accounts/${accountId}/sync`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

// =============================================================================
// Positions and Orders API
// =============================================================================

export interface AccountSummary {
  equity: string;
  cash: string;
  buying_power: string;
}

export interface PositionsResponse {
  positions: Position[];
  account: AccountSummary;
}

export async function getAccountPositions(accountId: string): Promise<PositionsResponse> {
  const response = await fetch(`${API_BASE}/api/v1/brokerage/accounts/${accountId}/positions`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<PositionsResponse>(response);
}

export async function getAccountOrders(
  accountId: string,
  status: string = "all"
): Promise<Order[]> {
  const response = await fetch(
    `${API_BASE}/api/v1/brokerage/accounts/${accountId}/orders?status=${status}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse<Order[]>(response);
}

export async function getOrderHistory(
  accountId: string,
  days: number = 30
): Promise<Order[]> {
  const response = await fetch(
    `${API_BASE}/api/v1/brokerage/accounts/${accountId}/order-history?days=${days}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse<Order[]>(response);
}

export async function getActiveInvestmentsCount(
  accountId: string
): Promise<{ count: number }> {
  const response = await fetch(
    `${API_BASE}/api/v1/brokerage/accounts/${accountId}/active-investments`,
    { headers: getAuthHeaders() }
  );
  return handleResponse<{ count: number }>(response);
}

// =============================================================================
// Scheduler API
// =============================================================================

export async function getSchedulerStatus(): Promise<SchedulerStatus> {
  const response = await fetch(`${API_BASE}/api/v1/execution/scheduler/status`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<SchedulerStatus>(response);
}

export async function triggerExecution(dryRun: boolean = false): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/api/v1/execution/scheduler/trigger-execution`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ dry_run: dryRun }),
  });
  return handleResponse(response);
}

export async function triggerSnapshot(): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/api/v1/execution/scheduler/trigger-snapshot`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

// =============================================================================
// Investment Execution API
// =============================================================================

export async function executeInvestment(
  investmentId: string,
  dryRun: boolean = false
): Promise<{ execution_run_id: string; status: string; message: string }> {
  const response = await fetch(`${API_BASE}/api/v1/execution/investments/${investmentId}/run-now`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ dryRun }),
  });
  return handleResponse(response);
}

export async function snapshotInvestment(investmentId: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/api/v1/execution/investments/${investmentId}/snapshot`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function getExecutionLogs(
  investmentId: string,
  limit: number = 20,
  offset: number = 0
): Promise<{ logs: ExecutionLog[]; total: number }> {
  const response = await fetch(
    `${API_BASE}/api/v1/execution/investments/${investmentId}/logs?limit=${limit}&offset=${offset}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse(response);
}

export async function getPerformanceSnapshots(
  investmentId: string,
  limit: number = 30
): Promise<PerformanceSnapshot[]> {
  const response = await fetch(
    `${API_BASE}/api/v1/execution/investments/${investmentId}/snapshots?limit=${limit}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse<PerformanceSnapshot[]>(response);
}

export async function getInvestmentPerformance(investmentId: string): Promise<{
  has_data: boolean;
  current?: PerformanceSnapshot;
  history?: PerformanceSnapshot[];
  total_return?: string;
  total_return_pct?: string;
  day_return?: string;
  day_return_pct?: string;
  portfolio_value?: string;
  cash_balance?: string;
  positions_count?: number;
  max_drawdown_pct?: string;
}> {
  const response = await fetch(
    `${API_BASE}/api/v1/execution/investments/${investmentId}/performance`,
    { headers: getAuthHeaders() }
  );
  return handleResponse(response);
}

export async function linkInvestmentToBrokerage(
  investmentId: string,
  brokerageAccountId: string
): Promise<{ success: boolean }> {
  const response = await fetch(
    `${API_BASE}/api/v1/execution/investments/${investmentId}/link-brokerage`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ brokerage_account_id: brokerageAccountId }),
    }
  );
  return handleResponse(response);
}
